// v3A — Your PILLS Customer ID card with regenerate + claim custom flow.
import React, { useState } from "react";
import { IdCard, Sparkles, RefreshCw, Copy, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";

const formatId = (id) => {
    if (!id) return "—";
    // Group the 14 digits as 4-4-3-3 for readability while keeping the PILLS prefix.
    const digits = id.slice(5);
    return `PILLS ${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 11)} ${digits.slice(11, 14)}`;
};

const PillsIdCard = () => {
    const { user, refresh } = useAuth();
    const [open, setOpen] = useState(false);
    const pid = user?.pills_id;
    const isFancy = !!user?.pills_id_is_fancy;
    const used = user?.pills_id_changes_used ?? 0;
    const remaining = Math.max(0, 3 - used);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(pid || "");
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Couldn't copy");
        }
    };

    return (
        <section
            className="rounded-3xl border border-zinc-100 bg-gradient-to-br from-brand-50 via-white to-purple-50 p-6 shadow-soft dark:border-white/10 dark:from-brand-900/20 dark:via-zinc-900 dark:to-purple-900/10"
            data-testid="pills-id-card"
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-brand-700 to-brand-400 text-white">
                        <IdCard className="h-6 w-6" />
                    </div>
                    <div>
                        <p className="label-uppercase">Your PILLS ID</p>
                        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{user?.full_name || "—"}</h2>
                    </div>
                </div>
                {isFancy && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700" data-testid="pills-id-fancy-badge">
                        <Sparkles className="h-3 w-3" /> Fancy
                    </span>
                )}
            </div>

            <div className="mt-5 rounded-2xl bg-zinc-900 px-5 py-4 text-white shadow-inner" data-testid="pills-id-display">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Customer ID</p>
                <p className="mt-1 font-mono text-xl font-semibold tracking-wider tabular-nums sm:text-2xl">{formatId(pid)}</p>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-white/10 dark:bg-white/5 dark:text-zinc-200" data-testid="pills-id-copy">
                    <Copy className="h-3 w-3" /> Copy
                </button>
                <button onClick={() => setOpen(true)} disabled={remaining === 0} className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50" data-testid="pills-id-change">
                    <RefreshCw className="h-3 w-3" /> Change ID
                </button>
                <p className="text-[11px] text-zinc-500" data-testid="pills-id-quota">
                    {remaining} of 3 changes remaining
                </p>
            </div>

            {open && <ChangeIdModal onClose={() => setOpen(false)} onSuccess={refresh} currentId={pid} remaining={remaining} />}
        </section>
    );
};

const ChangeIdModal = ({ onClose, onSuccess, currentId, remaining }) => {
    const [mode, setMode] = useState("auto"); // auto | custom
    const [customId, setCustomId] = useState("PILLS");
    const [avail, setAvail] = useState(null);
    const [busy, setBusy] = useState(false);

    const regenerate = async () => {
        setBusy(true);
        try {
            await api.post("/auth/pills-id/regenerate", {});
            toast.success("New fancy ID generated!");
            onSuccess?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not regenerate");
        } finally { setBusy(false); }
    };

    const check = async (val) => {
        setAvail(null);
        if (!/^PILLS\d{14}$/.test(val)) return;
        try {
            const { data } = await api.post("/auth/pills-id/check-availability", { requested_id: val });
            setAvail(data);
        } catch { setAvail({ available: false, reason: "ERROR", message: "Check failed" }); }
    };

    const claim = async () => {
        setBusy(true);
        try {
            await api.post("/auth/pills-id/claim", { requested_id: customId });
            toast.success("Your new ID is set!");
            onSuccess?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not claim ID");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/40 p-4" onClick={onClose} data-testid="pills-id-modal">
            <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="label-uppercase">Change PILLS ID</p>
                        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-50">Pick a new identity</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100" data-testid="pills-id-modal-close"><X className="h-4 w-4" /></button>
                </div>

                <div className="mt-4 flex rounded-full border border-zinc-200 p-1 text-xs font-medium dark:border-white/10">
                    <button onClick={() => setMode("auto")} className={`flex-1 rounded-full px-3 py-1.5 transition-colors ${mode === "auto" ? "bg-brand-700 text-white" : "text-zinc-600"}`} data-testid="pills-id-mode-auto">🎲 Generate fancy</button>
                    <button onClick={() => setMode("custom")} className={`flex-1 rounded-full px-3 py-1.5 transition-colors ${mode === "custom" ? "bg-brand-700 text-white" : "text-zinc-600"}`} data-testid="pills-id-mode-custom">Pick custom</button>
                </div>

                {mode === "auto" ? (
                    <div className="mt-5 text-sm text-zinc-600 dark:text-zinc-300">
                        <p>We'll generate a beautiful pattern-based ID (matching pairs, palindromes, sequences) and assign it to you.</p>
                        <p className="mt-2 text-xs text-zinc-500">Current: <span className="font-mono">{currentId}</span></p>
                        <button onClick={regenerate} disabled={busy} className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50" data-testid="pills-id-regenerate-submit">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            Generate new fancy ID
                        </button>
                    </div>
                ) : (
                    <div className="mt-5 space-y-3">
                        <label className="block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Custom ID (PILLS + 14 digits)</span>
                            <input
                                value={customId}
                                onChange={(e) => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 19); setCustomId(v); check(v); }}
                                className="mt-1 w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 font-mono text-sm outline-none ring-brand-200 focus:ring-2 dark:border-white/10 dark:bg-zinc-800"
                                placeholder="PILLS12345678901234"
                                data-testid="pills-id-custom-input"
                            />
                        </label>
                        {avail && (
                            <p className={`text-xs ${avail.available ? "text-emerald-700" : "text-rose-600"}`} data-testid="pills-id-availability">
                                {avail.available ? <><Check className="inline h-3 w-3" /> Available {avail.is_fancy ? "· 🌟 Fancy" : ""}</> : avail.message}
                            </p>
                        )}
                        <button onClick={claim} disabled={busy || !avail?.available} className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-brand-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50" data-testid="pills-id-claim-submit">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Claim this ID
                        </button>
                    </div>
                )}

                <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                    ⚠️ This change is permanent. You have <strong>{remaining}</strong> changes remaining.
                </p>
            </div>
        </div>
    );
};

export default PillsIdCard;
